import { IeltsListeningSubmitPartAttemptInput } from '@/__generated__/graphql';
import { AuthProviderViewerSessionQueryDocument, IeltsSkill } from '@/__generated__/graphql';
import useIeltsAttemptError from '@/components/ielts/common/hooks/useIeltsAttemptError';
import {
  IeltsListeningEndAttemptMutation,
  IeltsListeningSubmitPartAttemptMutation,
} from '@/api/common';
import {
  activeListeningPartAtom,
  activeQuestionIdAtom,
  attemptIdAtom,
  loadingSubmitIeltsAtom,
  selectedListeningPartsAtom,
} from '@/store/ieltsListeningReading';
import { mockTestViolationCountAtom } from '@/store/mockTest';
import { IeltsListeningReadingWritingFormValues } from '@/types';
import { useMutation } from '@apollo/client';
import { useAtom, useAtomValue } from 'jotai';
import { cloneDeep } from 'lodash-es';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

function useIeltsListening() {
  const router = useRouter();

  const [submitQuestionMutation] = useMutation(IeltsListeningSubmitPartAttemptMutation, {
    context: { v2: true },
  });

  const [endAttempt, { loading: loadingSubmit }] = useMutation(IeltsListeningEndAttemptMutation, {
    context: { v2: true },
  });

  const attemptId = useAtomValue(attemptIdAtom);
  const [activePart, setActivePart] = useAtom(activeListeningPartAtom);
  const [, setActiveQuestionId] = useAtom(activeQuestionIdAtom);
  const selectedParts = useAtomValue(selectedListeningPartsAtom);
  const [, setLoadingSubmit] = useAtom(loadingSubmitIeltsAtom);
  const violationCount = useAtomValue(mockTestViolationCountAtom);

  const { handleAttemptError, handleUserErrors } = useIeltsAttemptError({
    skill: IeltsSkill.Listening,
    attemptId,
  });

  const { setValue, getValues } = useFormContext<IeltsListeningReadingWritingFormValues>() || {};

  const handleSubmit = useCallback(
    async (input: {
      questionId: string;
      detailAnswer: IeltsListeningSubmitPartAttemptInput['detailAnswers'][number];
    }) => {
      if (!activePart?.node.id || !attemptId) return;
      const partIndex = selectedParts.findIndex((p) => p.node.id === activePart.node.id);
      const questionIndex = activePart.node.ieltsListeningPartQuestions.findIndex(
        (q) => q.node.id === input.questionId,
      );

      const detailAnswers = cloneDeep(
        getValues().parts[partIndex]?.questions[questionIndex]?.detailAnswers || [],
      );
      const detailIndex = detailAnswers.findIndex(
        (d) => d.detailId === input.detailAnswer.detailId,
      );

      if (detailIndex < 0) {
        return;
      }

      const existingDetailAnswers = cloneDeep(
        getValues().parts[partIndex]?.questions[questionIndex]?.detailAnswers || [],
      );

      detailAnswers[detailIndex] = input.detailAnswer;

      setValue(`parts.${partIndex}.questions.${questionIndex}.detailAnswers.${detailIndex}`, {
        ...existingDetailAnswers[detailIndex],
        ...input.detailAnswer,
      });

      try {
        await submitQuestionMutation({
          variables: {
            input: {
              questionId: input.questionId,
              detailAnswers: detailAnswers.map((d) => ({
                detailId: d.detailId,
                text: d.text?.trim(),
                optionId: d.optionId,
                optionIds: d.optionIds,
              })),
              attemptId,
              partId: activePart.node.id,
            },
          },
          onCompleted: (res) => {
            handleUserErrors(res.ieltsListeningSubmitPartAttempt.userErrors, 'submit');
          },
        });
      } catch (error) {
        // Answers autosave on every change, so a failure here silently loses the
        // learner's work unless we say so.
        handleAttemptError(error, 'submit');
      }
    },
    [
      activePart?.node.id,
      activePart?.node.ieltsListeningPartQuestions,
      attemptId,
      getValues,
      handleAttemptError,
      handleUserErrors,
      selectedParts,
      setValue,
      submitQuestionMutation,
    ],
  );

  const submitAttempt = useCallback(async () => {
    if (!attemptId || loadingSubmit) return;
    setLoadingSubmit(true);

    try {
      await endAttempt({
        variables: {
          input: {
            attemptId,
            violationCount,
          },
        },
        refetchQueries: [AuthProviderViewerSessionQueryDocument],
        awaitRefetchQueries: true,
        onCompleted: (res) => {
          if (handleUserErrors(res.ieltsListeningEndAttempt.userErrors, 'end')) {
            setLoadingSubmit(false);
            return;
          }
          const targetScreen = 'result';
          void router.push(`/ielts/listening/${targetScreen}/${attemptId}`);
        },
      });
    } catch (error) {
      setLoadingSubmit(false);
      handleAttemptError(error, 'end');
    }
  }, [
    attemptId,
    endAttempt,
    handleAttemptError,
    handleUserErrors,
    loadingSubmit,
    router,
    setLoadingSubmit,
    violationCount,
  ]);

  const canPrevPart = useMemo(() => {
    const currentIndex = selectedParts.findIndex((p) => p.position === activePart?.position);
    return currentIndex > 0;
  }, [activePart?.position, selectedParts]);

  const canNextPart = useMemo(() => {
    const currentIndex = selectedParts.findIndex((p) => p.position === activePart?.position);
    return currentIndex < selectedParts.length - 1;
  }, [activePart?.position, selectedParts]);

  const handlePrevPart = useCallback(() => {
    if (!canPrevPart || !activePart) return;
    const currentIndex = selectedParts.findIndex((p) => p.position === activePart.position);
    const prevPart = selectedParts[currentIndex - 1];

    if (!prevPart) return;

    setActiveQuestionId(undefined);
    setActivePart(prevPart);
  }, [activePart, canPrevPart, selectedParts, setActivePart, setActiveQuestionId]);

  const handleNextPart = useCallback(() => {
    if (!canNextPart || !activePart) return;
    const currentIndex = selectedParts.findIndex((p) => p.position === activePart.position);
    const nextPart = selectedParts[currentIndex + 1];
    if (!nextPart) return;

    setActiveQuestionId(undefined);
    setActivePart(nextPart);
  }, [activePart, canNextPart, selectedParts, setActivePart, setActiveQuestionId]);

  return useMemo(
    () => ({
      handleSubmit,
      submitAttempt,
      canNextPart,
      canPrevPart,
      handlePrevPart,
      handleNextPart,
    }),
    [canNextPart, canPrevPart, handleNextPart, handlePrevPart, handleSubmit, submitAttempt],
  );
}

export default useIeltsListening;
